import { maskMyNumber } from '../../GeneralProfile/useGeneralProfileFlow';
import { formatOtherIncomeChangeReasonDisplay } from '../otherIncomeEarner/otherIncomeEarnerDisplay';
import { changeReasonLabelKey, isSpouseChangeReasonCode } from '../spouseSection/spouseChangeReason';
import { parseIncomeYen } from '../spouseSection/spouseEligibility';
import { ageAtTaxYearEnd } from '../../family/familyInfoSummary';
import {
  isDependentChangeReasonCode,
  type DependentDraft,
  type DependentIneligibilityReason,
  type DependentsStep,
  type DependentTaxCategory,
} from './dependentsSectionTypes';

/** Standard 源泉控除対象親族 income ceiling (合計所得金額 58万円以下). */
export const DEPENDENT_INCOME_LIMIT_YEN = 580_000;
/** 特定親族 (ages 19–22) upper income ceiling (合計所得金額 100万円以下). */
export const SPECIFIC_RELATIVE_INCOME_MAX_YEN = 1_000_000;

export function stepQuestionKey(step: DependentsStep): string {
  return `submitDocuments.dependentDeduction.dependents.steps.${step}`;
}

/** Help text shown above the composer; empty string = no help. */
export function stepHelpKey(step: DependentsStep): string {
  switch (step) {
    case 'sharedLivelihood':
      return 'submitDocuments.dependentDeduction.dependents.help.sharedLivelihood';
    case 'nonResident':
      return 'submitDocuments.dependentDeduction.dependents.help.nonResident';
    case 'remittanceAmount':
      return 'submitDocuments.dependentDeduction.dependents.help.remittanceAmount';
    case 'estimatedIncome':
      return 'submitDocuments.dependentDeduction.dependents.help.estimatedIncome';
    default:
      return '';
  }
}

/** True when income exceeds the applicable ceiling for this dependent's age. */
export function incomeOverLimit(estimatedIncome: string, age: number | null = null): boolean {
  const income = parseIncomeYen(estimatedIncome);
  if (age != null && age >= 19 && age <= 22) {
    return income > SPECIFIC_RELATIVE_INCOME_MAX_YEN;
  }
  return income > DEPENDENT_INCOME_LIMIT_YEN;
}

export function dependentAge(d: DependentDraft, taxYear: number): number | null {
  return ageAtTaxYearEnd(d.dateOfBirth, taxYear);
}

/**
 * Non-resident dependents aged 30–69 qualify only via study abroad, disability,
 * or remittances of ¥380,000+ (2023 rule).
 */
export function nonResidentNoneIneligible(d: DependentDraft, taxYear: number): boolean {
  if (d.nonResident !== 'yes' || d.nonResidentReason !== 'none') return false;
  const age = dependentAge(d, taxYear);
  if (age == null) return false;
  return age >= 30 && age < 70;
}

function isParentLike(relationship: string): boolean {
  return relationship === 'father' || relationship === 'mother';
}

/** Holistic eligibility check over all gate answers ('' = eligible so far). */
export function deriveIneligibility(
  d: DependentDraft,
  taxYear: number
): DependentIneligibilityReason {
  if (d.sharedLivelihood === 'no') return 'no_shared_livelihood';
  if (incomeOverLimit(d.estimatedIncome, dependentAge(d, taxYear))) return 'income_over_limit';
  if (nonResidentNoneIneligible(d, taxYear)) return 'non_resident_no_support';
  return '';
}

/**
 * Auto-derived classification from DOB (age as of Dec 31 of the tax year) and income.
 * Aligns with 扶養控除区分: under16 / specific / specific_relative / elderly / general.
 */
export function deriveTaxCategories(d: DependentDraft, taxYear: number): DependentTaxCategory[] {
  const age = dependentAge(d, taxYear);
  const income = parseIncomeYen(d.estimatedIncome);
  const out: DependentTaxCategory[] = [];
  if (age != null) {
    if (age < 16) {
      out.push('under16');
    } else if (age >= 19 && age <= 22) {
      if (income <= DEPENDENT_INCOME_LIMIT_YEN) {
        out.push('specific');
      } else if (income <= SPECIFIC_RELATIVE_INCOME_MAX_YEN) {
        out.push('specific_relative');
      }
    } else if (age >= 70) {
      out.push('elderly');
      if (d.livingInSameHousehold === 'yes' && isParentLike(d.relationship)) {
        out.push('cohabiting_elderly_parents');
      }
    } else {
      out.push('general');
    }
  }
  if (d.nonResident === 'yes') out.push('non_residents');
  return out;
}

/** Steps reached after all gates passed (per-dependent Phase B order). */
export function nextStepAfter(current: DependentsStep, d: DependentDraft, taxYear: number): DependentsStep {
  switch (current) {
    case 'addName':
      return 'addKana';
    case 'addKana':
      return 'addRelationship';
    case 'addRelationship':
      return d.relationship === 'other' ? 'addRelationshipOther' : 'addDob';
    case 'addRelationshipOther':
      return 'addDob';
    case 'addDob':
      return 'myNumber';
    case 'confirmFullName':
      return 'confirmFullNameKana';
    case 'confirmFullNameKana':
      return 'confirmRelationship';
    case 'confirmRelationship':
      return 'myNumber';
    case 'myNumber':
      return 'sameHousehold';
    case 'sameHousehold':
      return d.livingInSameHousehold === 'no' ? 'addressIfNot' : 'sharedLivelihood';
    case 'addressIfNot':
      return 'sharedLivelihood';
    case 'sharedLivelihood':
      return d.sharedLivelihood === 'no' ? 'ineligibleNotice' : 'estimatedIncome';
    case 'estimatedIncome':
      return incomeOverLimit(d.estimatedIncome, dependentAge(d, taxYear))
        ? 'ineligibleNotice'
        : 'ageCategoryNotice';
    case 'ageCategoryNotice':
      return 'nonResident';
    case 'nonResident':
      return d.nonResident === 'yes' ? 'nonResidentReason' : 'disabilityStatus';
    case 'nonResidentReason':
      if (d.nonResidentReason === 'remittance_380k') return 'remittanceAmount';
      if (nonResidentNoneIneligible(d, taxYear)) return 'ineligibleNotice';
      return 'disabilityStatus';
    case 'remittanceAmount':
      return 'disabilityStatus';
    case 'disabilityStatus':
      return 'changeDuringYear';
    case 'changeDuringYear':
      return d.changeDuringYear === 'yes' ? 'changeDate' : 'addAnother';
    case 'changeDate':
      return 'changeReason';
    case 'changeReason':
      return d.changeReason === 'other' ? 'changeReasonOther' : 'addAnother';
    case 'changeReasonOther':
      return 'addAnother';
    case 'ineligibleNotice':
      return 'addAnother';
    default:
      return current;
  }
}

/** Branch-aware previous step within the current dependent (for undo). */
export function prevStepForUndo(current: DependentsStep, d: DependentDraft): DependentsStep | null {
  const identityLast = d.addedInSection ? 'addDob' : 'confirmRelationship';
  switch (current) {
    case 'addKana':
      return 'addName';
    case 'addRelationship':
      return 'addKana';
    case 'addRelationshipOther':
      return 'addRelationship';
    case 'addDob':
      return d.relationship === 'other' ? 'addRelationshipOther' : 'addRelationship';
    case 'confirmFullNameKana':
      return 'confirmFullName';
    case 'confirmRelationship':
      return 'confirmFullNameKana';
    case 'myNumber':
      return identityLast;
    case 'sameHousehold':
      return 'myNumber';
    case 'addressIfNot':
      return 'sameHousehold';
    case 'sharedLivelihood':
      return d.livingInSameHousehold === 'no' ? 'addressIfNot' : 'sameHousehold';
    case 'estimatedIncome':
      return 'sharedLivelihood';
    case 'ageCategoryNotice':
      return 'estimatedIncome';
    case 'nonResident':
      return 'ageCategoryNotice';
    case 'nonResidentReason':
      return 'nonResident';
    case 'remittanceAmount':
      return 'nonResidentReason';
    case 'disabilityStatus':
      if (d.nonResident === 'yes') {
        return d.nonResidentReason === 'remittance_380k' ? 'remittanceAmount' : 'nonResidentReason';
      }
      return 'nonResident';
    case 'changeDuringYear':
      return 'disabilityStatus';
    case 'changeDate':
      return 'changeDuringYear';
    case 'changeReason':
      return 'changeDate';
    case 'changeReasonOther':
      return 'changeReason';
    case 'ineligibleNotice':
      if (d.ineligibilityReason === 'no_shared_livelihood') return 'sharedLivelihood';
      if (d.ineligibilityReason === 'income_over_limit') return 'estimatedIncome';
      if (d.ineligibilityReason === 'non_resident_no_support') return 'nonResidentReason';
      return null;
    default:
      return null;
  }
}

/** Whether the draft already holds an answer for the given input step. */
export function stepHasAnswer(step: DependentsStep, d: DependentDraft): boolean {
  const yn = (v: string) => v === 'yes' || v === 'no';
  switch (step) {
    case 'addName':
    case 'confirmFullName':
      return Boolean(d.fullName.trim());
    case 'addKana':
    case 'confirmFullNameKana':
      return Boolean(d.fullNameKana.trim());
    case 'addRelationship':
    case 'confirmRelationship':
      return Boolean(d.relationship);
    case 'addRelationshipOther':
      return Boolean(d.relationshipOther.trim());
    case 'addDob':
      return Boolean(d.dateOfBirth);
    case 'myNumber':
      return d.myNumber.replace(/\D/g, '').length === 12;
    case 'sameHousehold':
      return yn(d.livingInSameHousehold);
    case 'addressIfNot':
      return Boolean(d.ifNotAddress.trim());
    case 'sharedLivelihood':
      return yn(d.sharedLivelihood);
    case 'estimatedIncome':
      return Boolean(d.estimatedIncome.trim());
    case 'nonResident':
      return yn(d.nonResident);
    case 'nonResidentReason':
      return Boolean(d.nonResidentReason);
    case 'remittanceAmount':
      return Boolean(d.remittanceAmount.trim());
    case 'disabilityStatus':
      return Boolean(d.disabilityCategory);
    case 'changeDuringYear':
      return yn(d.changeDuringYear);
    case 'changeDate':
      return Boolean(d.changeDate);
    case 'changeReason':
      return Boolean(d.changeReason.trim());
    case 'changeReasonOther':
      return Boolean(d.changeReason.trim()) && d.changeReason !== 'other';
    default:
      return false;
  }
}

const WALK_LIMIT = 40;

/**
 * Walk the branch-aware flow from `start`, skipping steps already answered
 * (and notices already reflected in the draft). Returns the first step still
 * needing input, or 'addAnother' when the whole path is answered.
 * Used for isolated field edits from the expanded summary review.
 */
export function firstMissingStepFrom(
  start: DependentsStep,
  d: DependentDraft,
  taxYear: number
): DependentsStep {
  let s = start;
  for (let i = 0; i < WALK_LIMIT; i++) {
    if (s === 'addAnother') return s;
    if (s === 'ageCategoryNotice' || (s === 'ineligibleNotice' && !d.eligible)) {
      s = nextStepAfter(s, d, taxYear);
      continue;
    }
    if (!stepHasAnswer(s, d)) return s;
    s = nextStepAfter(s, d, taxYear);
  }
  return s;
}

/**
 * Branch-aware committed set for a saved dependent snapshot, so an expanded
 * summary shows exactly the questions that were answered on its path.
 */
export function committedStepsForSnapshot(
  d: DependentDraft,
  taxYear: number
): DependentsStep[] {
  const out: DependentsStep[] = [];
  let s: DependentsStep = d.addedInSection ? 'addName' : 'confirmFullName';
  for (let i = 0; i < WALK_LIMIT; i++) {
    if (s === 'addAnother') break;
    if (s === 'ageCategoryNotice') {
      out.push(s);
      s = nextStepAfter(s, d, taxYear);
      continue;
    }
    if (s === 'ineligibleNotice') {
      if (!d.eligible) out.push(s);
      s = nextStepAfter(s, d, taxYear);
      continue;
    }
    if (!stepHasAnswer(s, d)) break;
    out.push(s);
    s = nextStepAfter(s, d, taxYear);
  }
  return out;
}

export function relationshipLabel(t: (k: string) => string, d: DependentDraft): string {
  if (!d.relationship) return '';
  if (d.relationship === 'other') {
    return d.relationshipOther.trim() || t('submitDocuments.dependentDeduction.dependents.relationship.other');
  }
  if (d.relationship === 'child' || d.relationship === 'father' || d.relationship === 'mother') {
    return t(`submitDocuments.familyInfo.relationship.${d.relationship}`);
  }
  return d.relationship;
}

export function changeReasonAnswer(t: (k: string) => string, d: DependentDraft): string {
  const v = d.changeReason.trim();
  if (!v) return '';
  if (isDependentChangeReasonCode(v)) {
    return t(`submitDocuments.dependentDeduction.dependents.changeReason.option.${v}`);
  }
  if (isSpouseChangeReasonCode(v)) {
    return t(changeReasonLabelKey(v));
  }
  return formatOtherIncomeChangeReasonDisplay(t, v);
}

export function userAnswerForStep(
  step: DependentsStep,
  d: DependentDraft,
  t: (k: string) => string
): string {
  const yn = (v: string) =>
    v === 'yes'
      ? t('submitDocuments.dependentDeduction.spouse.yes')
      : v === 'no'
        ? t('submitDocuments.dependentDeduction.spouse.no')
        : '';
  switch (step) {
    case 'addName':
    case 'confirmFullName':
      return d.fullName.trim();
    case 'addKana':
    case 'confirmFullNameKana':
      return d.fullNameKana.trim();
    case 'addRelationship':
    case 'confirmRelationship':
      return relationshipLabel(t, d);
    case 'addRelationshipOther':
      return d.relationshipOther.trim();
    case 'addDob':
      return d.dateOfBirth;
    case 'myNumber':
      return maskMyNumber(d.myNumber);
    case 'sameHousehold':
      return yn(d.livingInSameHousehold);
    case 'addressIfNot':
      return d.ifNotAddress.trim();
    case 'sharedLivelihood':
      return yn(d.sharedLivelihood);
    case 'estimatedIncome':
      return d.estimatedIncome.trim();
    case 'nonResident':
      return yn(d.nonResident);
    case 'nonResidentReason':
      return d.nonResidentReason
        ? t(`submitDocuments.dependentDeduction.dependents.nonResidentReason.option.${d.nonResidentReason}`)
        : '';
    case 'remittanceAmount':
      return d.remittanceAmount.trim();
    case 'disabilityStatus':
      return d.disabilityCategory
        ? t(`submitDocuments.profileName.disabilityCategory.option.${d.disabilityCategory}`)
        : '';
    case 'changeDuringYear':
      return yn(d.changeDuringYear);
    case 'changeDate':
      return d.changeDate;
    case 'changeReason':
      return changeReasonAnswer(t, d);
    case 'changeReasonOther':
      return d.changeReason.trim();
    default:
      return '';
  }
}
